
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Info, Plus, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import NotificationForm from "./NotificationForm";

interface NotificationItem {
  id: string;
  title: string;
  date: string;
}

const NotificationSection = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const isMobileMenu = isMobile && document.querySelector('.mobile-notifications') !== null;
  
  // Fetch notifications from the database
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('board_posts')
        .select('id, title, created_at')
        .eq('category', '공지사항')
        .order('created_at', { ascending: false })
        .limit(5);
        
      if (error) throw error;
      
      return data.map(item => ({
        id: item.id,
        title: item.title,
        date: new Date(item.created_at).toISOString().split('T')[0]
      }));
    },
  });

  const toggleForm = () => setShowForm(!showForm);

  const handleFormSuccess = () => {
    setShowForm(false);
  };

  // Function to check if user can write notices - only allow ilwoonge@gmail.com
  const canWriteNotice = () => {
    return user !== null && user.email === 'ilwoonge@gmail.com';
  };

  if (isMobileMenu) {
    // Simplified version for mobile menu
    return (
      <ul className="space-y-1.5">
        {isLoading ? (
          <li className="text-sm">로딩 중...</li>
        ) : notifications.length > 0 ? (
          notifications.map((notification) => (
            <li key={notification.id} className="border-b pb-1.5 last:border-b-0 last:pb-0">
              <a href={`/board/post/${notification.id}`} className="flex hover:text-primary transition-colors">
                <span className="line-clamp-1 text-sm">{notification.title}</span>
              </a>
            </li>
          ))
        ) : (
          <li className="text-sm text-muted-foreground">등록된 공지사항이 없습니다.</li>
        )}
      </ul>
    );
  }

  // Original card version for desktop
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2 pt-3">
        <CardTitle className="text-base font-medium flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <Info size={16} className="text-primary" />
            공지사항
          </div>
          {canWriteNotice() && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleForm}
              className="h-8 w-8 p-0"
            >
              {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              <span className="sr-only">{showForm ? '취소' : '공지사항 작성'}</span>
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 py-2">
        {showForm ? (
          <div className="mb-4">
            <NotificationForm 
              onSuccess={handleFormSuccess} 
              onCancel={() => setShowForm(false)} 
            />
          </div>
        ) : null}
        
        {isLoading ? (
          <p className="text-sm text-center py-2">로딩 중...</p>
        ) : (
          <ul className="space-y-1.5">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <li key={notification.id} className="border-b pb-1.5 last:border-b-0 last:pb-0">
                  <a href={`/board/post/${notification.id}`} className="flex hover:text-primary transition-colors">
                    <span className="line-clamp-1 text-sm">{notification.title}</span>
                  </a>
                </li>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-2">등록된 공지사항이 없습니다.</p>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSection;
